import { useCallback, useEffect, useMemo, useState } from 'react';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, fmt, fmtDate, fmtTimestamp, type CardPart, type FloorStage, type QueueItem } from '../api.ts';
import CorrugationCardDialog from '../components/CorrugationCardDialog.tsx';
import Toast from '../components/Toast.tsx';
import UndoDialog from '../components/UndoDialog.tsx';
import MovePartDialog, { type MoveResult as PartResult } from '../components/MovePartDialog.tsx';
import MoveOnDialog from '../components/MoveOnDialog.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import SendOutsideDialog from '../components/SendOutsideDialog.tsx';
import SendOutDialog, { type SendResult } from '../components/SendOutDialog.tsx';
import ReceiveBackDialog, { type ReceiveResult } from '../components/ReceiveBackDialog.tsx';

interface NextStage { name: string; isDispatch: boolean }
interface MoveResult { jobComplete: boolean; nextStage: NextStage | null }

const FILTER_KEY = 'opal.production.filter';

/** The corrugation card is still to fill in, and Corrugation has not started (067). */
const corrugationToDo = (it: QueueItem) =>
  Boolean(it.corrugation_required && !it.corrugation_ready && !it.corrugation_locked);

const floorSince = (s: string | null | undefined) => {
  if (!s) return '—';
  const t = fmtTimestamp(s);
  const year = ` ${new Date(s).getFullYear()},`;
  return new Date(s).getFullYear() === new Date().getFullYear() ? t.replace(year, ',') : t;
};

/**
 * The whole floor, one screen, one row per job at wherever it currently
 * stands. The dispatch stage is never a row here -- that hand-off happens
 * automatically the moment Packing finishes, and shipping it is a different
 * screen's job, not a dead-end tab on this one.
 */
export default function Production({
  stages,
  onOpenCard,
  onChanged,
  onDispatchReady,
}: {
  stages: FloorStage[];
  /** Opens the job card at the card this stage works from (067). */
  onOpenCard: (jobId: number, part?: CardPart, edit?: boolean) => void;
  /** Work moved: the counts in the Production nav badge need re-reading. */
  onChanged: () => void;
  /** Packing just finished on this job -- offer to jump straight to Dispatch. */
  onDispatchReady: (jobNumber: string) => void;
}) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{
    text: string; dispatchJob?: string; undoJobId?: number; undoUrl?: string; cardJobId?: number;
  } | null>(null);
  const [moveFor, setMoveFor] = useState<QueueItem | null>(null);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [moveOnFor, setMoveOnFor] = useState<QueueItem | null>(null);
  const [menuFor, setMenuFor] = useState<{ item: QueueItem; right: number; top?: number; bottom?: number } | null>(null);
  const [acting, setActing] = useState<{ item: QueueItem; kind: 'HOLD' | 'SKIP' } | null>(null);
  const [reason, setReason] = useState('');
  /** Job work (091): the plan window, Send out, Receive back, and undoing a send out. */
  const [planFor, setPlanFor] = useState<{ jobId: number; jobStageId?: number } | null>(null);
  const [sendFor, setSendFor] = useState<QueueItem | null>(null);
  const [receiveFor, setReceiveFor] = useState<QueueItem | null>(null);
  const [undoOutFor, setUndoOutFor] = useState<QueueItem | null>(null);
  /** The corrugation card window (067), for a job still pending it. */
  const [corrFor, setCorrFor] = useState<number | null>(null);
  /** One stage's jobs, or the ones on hold or late. Kept per screen, so the
   *  corrugation operator's screen keeps showing Corrugation. */
  const [filter, setFilter] = useState<string>(() => {
    try { return localStorage.getItem(FILTER_KEY) ?? 'ALL'; } catch { return 'ALL'; }
  });
  const pickFilter = (v: string) => {
    setFilter(v);
    try { localStorage.setItem(FILTER_KEY, v); } catch { /* the filter still applies */ }
  };

  const noticeKey = useMemo(() => Date.now(), [notice]);
  const stageById = useMemo(() => new Map(stages.map((s) => [s.id, s])), [stages]);

  const load = useCallback(async () => {
    setError('');
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      const res = await api.get<{ items: QueueItem[] }>(`/api/production/queue?${qs}`);
      setItems(res.items.filter((it) => !stageById.get(it.stage_id)?.is_dispatch));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the floor');
    } finally {
      setLoading(false);
    }
  }, [search, stageById]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
    onChanged();
  }, [load, onChanged]);

  function afterPart(r: PartResult) {
    const undo = `/api/production/moves/${r.splitId}/undo`;
    setNotice(r.nextStage?.isDispatch
      ? { text: `${r.part.jobNumber} ready to dispatch · ${fmt(r.part.quantity)}`,
          dispatchJob: r.part.jobNumber, undoUrl: undo }
      : { text: `${r.part.jobNumber} at ${r.nextStage?.name ?? ''} · ${fmt(r.part.quantity)} · ${r.rest.jobNumber} keeps ${fmt(r.rest.quantity)}`,
          cardJobId: r.part.id, undoUrl: undo });
  }

  function afterSend(item: QueueItem, r: SendResult) {
    setNotice(r.part && r.rest
      ? { text: `${r.part.jobNumber} (${fmt(r.part.quantity)}) sent to ${r.worker} · ${r.rest.jobNumber} keeps ${fmt(r.rest.quantity)}`,
          cardJobId: r.part.id }
      : { text: `${item.job_number} sent to ${r.worker} · ${fmt(r.quantity)}` });
  }

  function afterReceive(item: QueueItem, r: ReceiveResult) {
    const text = `${r.jobNumber} back from ${item.outside_worker ?? 'the job worker'} · ${fmt(r.quantity)}${r.wastage > 0 ? ` · wastage ${fmt(r.wastage)}` : ''}`;
    setNotice(r.nextStage?.isDispatch ? { text, dispatchJob: r.jobNumber } : { text });
  }

  async function removePlan(item: QueueItem) {
    setError(''); setNotice(null);
    try {
      await api.del(`/api/outside/${item.outside_id}`);
      setNotice({ text: `${item.job_number} will be done here: the outside plan is removed` });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the plan');
    }
  }

  async function undoSend(item: QueueItem) {
    setBusyId(item.job_stage_id); setError('');
    try {
      await api.post(`/api/outside/${item.outside_id}/undo`, {});
      setNotice({ text: `Send out undone · ${item.job_number} is planned to go to ${item.outside_worker ?? 'the job worker'} again` });
      setUndoOutFor(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not undo the send out');
      setUndoOutFor(null);
    } finally {
      setBusyId(null);
    }
  }

  function afterMove(item: QueueItem, res: MoveResult) {
    const jobNumber = item.job_number;
    const undoJobId = item.job_id;
    if (res.nextStage?.isDispatch) {
      setNotice({ text: `${jobNumber} packed — ready to dispatch`, dispatchJob: jobNumber, undoJobId });
    } else if (res.jobComplete) {
      setNotice({ text: `${jobNumber} is complete`, undoJobId });
    } else if (res.nextStage) {
      setNotice({ text: `${jobNumber} moved to ${res.nextStage.name}`, undoJobId });
    } else {
      setNotice({ text: `${jobNumber} updated`, undoJobId });
    }
  }

  /**
   * The one primary action, labelled with wherever the job is actually going.
   * A stage that asks for a quantity opens the dialog (starting the stage
   * first if it has not begun); everything else completes in one call --
   * completeStage already starts-then-finishes in a single transaction, so
   * there is nothing to do here but ask for it.
   */
  async function move(item: QueueItem) {
    setBusyId(item.job_stage_id);
    setError(''); setNotice(null);
    try {
      const stage = stageById.get(item.stage_id);
      if (stage?.asks_quantity) {
        setMoveOnFor(item);
        return;
      }
      const res = await api.post<MoveResult>(`/api/production/job-stages/${item.job_stage_id}/complete`);
      afterMove(item, res);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not move the job forward');
    } finally {
      setBusyId(null);
    }
  }

  async function resume(item: QueueItem) {
    setBusyId(item.job_stage_id);
    setError(''); setNotice(null);
    try {
      await api.post(`/api/production/job-stages/${item.job_stage_id}/resume`);
      setNotice({ text: `${item.job_number} resumed` });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resume the stage');
    } finally {
      setBusyId(null);
    }
  }

  async function doHold(item: QueueItem) {
    setBusyId(item.job_stage_id);
    setError('');
    try {
      await api.post(`/api/production/job-stages/${item.job_stage_id}/hold`, { reason });
      setNotice({ text: `${item.job_number} on hold` });
      setActing(null); setReason('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not hold the stage');
    } finally {
      setBusyId(null);
    }
  }

  async function doSkip(item: QueueItem) {
    setBusyId(item.job_stage_id);
    setError('');
    try {
      const res = await api.post<MoveResult>(`/api/production/job-stages/${item.job_stage_id}/skip`, { reason });
      afterMove(item, res);
      setActing(null); setReason('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not skip the stage');
    } finally {
      setBusyId(null);
    }
  }

  // The filters: each stage that has jobs on it, in route order, then the two
  // that need attention. Counts come from the same list the table shows.
  const stageChips = [...new Map(items.map((it) => [it.stage_id, it.stage_name])).entries()]
    .sort((a, b) => (stageById.get(a[0])?.sequence_no ?? 0) - (stageById.get(b[0])?.sequence_no ?? 0))
    .map(([id, name]) => ({ key: `STAGE:${id}`, label: name,
                            n: items.filter((it) => it.stage_id === id).length }));
  const chips = [
    { key: 'ALL', label: 'All', n: items.length },
    ...stageChips,
    { key: 'OUTSIDE', label: 'Outside', n: items.filter((it) => it.outside_status === 'OUT').length },
    { key: 'HOLD', label: 'On hold', n: items.filter((it) => it.status === 'ON_HOLD').length },
    { key: 'LATE', label: 'Late', n: items.filter((it) => it.delivery_state === 'LATE').length },
  ].filter((c) => c.key === 'ALL' || c.n > 0 || c.key === filter);
  const shown = items.filter((it) =>
    filter === 'ALL' ? true
    : filter === 'HOLD' ? it.status === 'ON_HOLD'
    : filter === 'OUTSIDE' ? it.outside_status === 'OUT'
    : filter === 'LATE' ? it.delivery_state === 'LATE'
    : `STAGE:${it.stage_id}` === filter);

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <span className="count">
            {loading ? 'loading…' : `${items.length} job${items.length === 1 ? '' : 's'} on the floor`}
          </span>
          <div className="spacer" />
          <input className="search" placeholder="Search company, box, job or PO"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {items.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '12px 16px 0' }}>
            {chips.map((c) => (
              <button key={c.key} type="button" className={`chip${filter === c.key ? ' on' : ''}`}
                      aria-pressed={filter === c.key} onClick={() => pickFilter(c.key)}>
                {c.label} <span className="chip-n">{c.n}</span>
              </button>
            ))}
          </div>
        )}

        {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
        {notice && (
          <Toast key={noticeKey} onClose={() => setNotice(null)}>
            <span>{notice.text}</span>
            {notice.dispatchJob && (
              <button className="toast-act" onClick={() => onDispatchReady(notice.dispatchJob!)}>
                Go to Dispatch
              </button>
            )}
            {notice.cardJobId && (
              <button className="toast-act" onClick={() => onOpenCard(notice.cardJobId!)}>
                Open card
              </button>
            )}
            {(notice.undoUrl || notice.undoJobId) && (
              <button className="toast-act"
                      onClick={() => {
                        setUndoUrl(notice.undoUrl ?? `/api/production/jobs/${notice.undoJobId}/undo`);
                        setNotice(null);
                      }}>
                Undo
              </button>
            )}
          </Toast>
        )}

        {!loading && items.length === 0 ? (
          <div className="empty">Nothing on the floor right now.</div>
        ) : !loading && shown.length === 0 ? (
          <div className="empty">
            Nothing here now.{' '}
            <button className="linkish" onClick={() => pickFilter('ALL')}>Show all</button>
          </div>
        ) : (
          <div className="tbl">
            <table className="fit">
              <thead>
                <tr>
                  <th>Job</th><th>Company</th><th>Box</th><th>Stage</th>
                  <th className="num">Route</th>
                  <th className="num">Left</th>
                  <th>Due</th>
                  <th>On floor since</th>
                  <th className="pin-right" />
                </tr>
              </thead>
              <tbody>
                {shown.map((it) => {
                  const rowClass =
                    it.delivery_state === 'LATE' ? 'late'
                    : it.delivery_state === 'DUE_SOON' ? 'soon' : '';
                  const isHeld = it.status === 'ON_HOLD';
                  const isRunning = it.status === 'IN_PROGRESS';
                  const busy = busyId === it.job_stage_id;
                  return (
                    <tr key={it.job_stage_id} className={rowClass}>
                      <td>
                        <button className="btn" style={{ padding: '2px 8px' }}
                                title={it.card_part === 'CORRUGATION' ? 'Open the corrugation card'
                                  : it.card_part === 'PRINTING' ? 'Open the printing card' : 'Open the job card'}
                                onClick={() => onOpenCard(it.job_id, it.card_part ?? undefined)}>
                          {it.job_number}
                        </button>
                        <MakeTag number={it.job_number} />
                        {corrugationToDo(it) && it.card_part !== 'CORRUGATION' && (
                          <div style={{ marginTop: 2 }}>
                            <button className="linkish" style={{ fontSize: 12, color: 'var(--soon)', whiteSpace: 'nowrap' }}
                                    title="Complete it before the job reaches Corrugation — the ply, and every layer's GSM and BF"
                                    onClick={() => setCorrFor(it.job_id)}>
                              Corrugation card pending →
                            </button>
                          </div>
                        )}
                        {/* Sent on without its board, until that is settled. */}
                        {it.released_short && Number(it.board_pending ?? 0) > 0 && (
                          <div><span className="tag waiting" style={{ marginTop: 4 }}
                                     title="Released without its board — issue it from Stock › Board › To order when it arrives">
                            No board · {fmt(it.board_pending!)} to come
                          </span></div>
                        )}
                        {it.borrowed_from && Number(it.borrowed_owed ?? 0) > 0 && (
                          <div className="dim" style={{ fontSize: 12, marginTop: 2 }}
                               title={`Uses board held for ${it.borrowed_from}, still to be given back`}>
                            Board from {it.borrowed_from}
                          </div>
                        )}
                      </td>
                      <td>{it.customer_name}</td>
                      <td>{it.box_name}</td>
                      <td>
                        {it.outside_status ? (it.outside_stage_names ?? it.stage_name) : it.stage_name}
                        {isRunning && <span className="tag high" style={{ marginLeft: 6 }}>Running</span>}
                        {isHeld && (
                          <div className="hint">
                            On hold{it.hold_reason ? `: ${it.hold_reason}` : ''}
                          </div>
                        )}
                        {it.outside_status === 'PLANNED' && (
                          <div className="out-where">
                            <span className="tag waiting">Outside</span>
                            <span className="dim">
                              {it.outside_worker}
                              {it.outside_planned_quantity
                                ? ` · ${fmt(it.outside_planned_quantity)} of ${fmt(it.ordered_quantity)}` : ''}
                            </span>
                          </div>
                        )}
                        {it.outside_status === 'OUT' && (() => {
                          const late = Boolean(it.outside_expected_back && it.outside_expected_back < new Date().toISOString().slice(0, 10));
                          return (
                            <div className="out-where">
                              <span className="tag waiting">At {it.outside_worker}</span>
                              <span className={late ? 'late-text' : 'dim'}>
                                sent {fmtDate(it.outside_sent_on)}
                                {it.outside_expected_back ? ` · back ${fmtDate(it.outside_expected_back)}${late ? ' · late' : ''}` : ''}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="num dim">{it.route_done}/{it.route_length}</td>
                      <td className="num"><strong>{fmt(it.pending_quantity)}</strong></td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtDate(it.delivery_date)}</td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                        {floorSince(it.released_at)}
                      </td>
                      <td className="pin-right">
                        {isHeld ? (
                          <button className="btn primary" disabled={busy} onClick={() => resume(it)}>
                            {busy ? 'Resuming…' : 'Resume'}
                          </button>
                        ) : it.outside_status === 'PLANNED' ? (
                          <button className="btn primary" onClick={() => setSendFor(it)}>
                            Send out to {it.outside_worker}
                          </button>
                        ) : it.outside_status === 'OUT' ? (
                          <button className="btn primary" onClick={() => setReceiveFor(it)}>
                            Receive from {it.outside_worker}
                          </button>
                        ) : it.card_part === 'CORRUGATION' && it.card_required && corrugationToDo(it) ? (
                          <button className="btn dot-soon" title="Add the ply and every layer's GSM and BF, then move it on"
                                  onClick={() => setCorrFor(it.job_id)}>
                            Complete corrugation card
                          </button>
                        ) : (
                          <button className="btn primary" disabled={busy}
                                  onClick={() => void move(it)}>
                            {busy ? 'Working…'
                              : it.next_stage_name ? `Move to ${it.next_stage_name}` : 'Finish'}
                          </button>
                        )}
                        <button className="btn" style={{ marginLeft: 6 }}
                                title="More actions" aria-haspopup="menu"
                                onClick={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  const right = window.innerWidth - r.right;
                                  setMenuFor(window.innerHeight - r.bottom < 160
                                    ? { item: it, right, bottom: window.innerHeight - r.top + 6 }
                                    : { item: it, right, top: r.bottom + 6 });
                                }}>
                          ⋯
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {planFor && (
        <SendOutsideDialog jobId={planFor.jobId}
                           {...(planFor.jobStageId ? { preselect: planFor.jobStageId } : {})}
                           onClose={() => setPlanFor(null)}
                           onDone={(msg) => { setNotice({ text: msg }); void refresh(); }} />
      )}
      {sendFor && (
        <SendOutDialog item={sendFor} onClose={() => setSendFor(null)}
                       onDone={(r) => { afterSend(sendFor, r); void refresh(); }} />
      )}
      {receiveFor && (
        <ReceiveBackDialog item={receiveFor} onClose={() => setReceiveFor(null)}
                           onDone={(r) => { afterReceive(receiveFor, r); void refresh(); }} />
      )}
      {undoOutFor && (
        <ConfirmDialog title={`Undo send out of ${undoOutFor.job_number}?`}
                       subtitle={`${undoOutFor.outside_stage_names ?? undoOutFor.stage_name} · ${undoOutFor.outside_worker ?? ''}`}
                       confirmLabel="Undo send out" busy={busyId === undoOutFor.job_stage_id}
                       onConfirm={() => void undoSend(undoOutFor)} onCancel={() => setUndoOutFor(null)}>
          <ul className="undo-list">
            <li>The goods did not leave: it goes back to a plan for {undoOutFor.outside_worker}</li>
            <li>If the job was split to send part, the split is undone and the part goes back into the job</li>
          </ul>
        </ConfirmDialog>
      )}
      {corrFor !== null && (
        <CorrugationCardDialog jobId={corrFor}
                               onClose={() => setCorrFor(null)}
                               onDone={(msg) => { setCorrFor(null); setNotice({ text: msg }); void refresh(); }} />
      )}
      {moveOnFor && (
        <MoveOnDialog
          item={moveOnFor}
          onClose={() => setMoveOnFor(null)}
          onDone={(r) => {
            if (r.kind === 'SPLIT') afterPart(r);
            else {
              afterMove(moveOnFor, r);
              if (r.shortOfOrder > 0) {
                setNotice({ text: `${moveOnFor.job_number} moved on · ${fmt(r.quantity)} · ${fmt(r.shortOfOrder)} short of the order`,
                            undoJobId: moveOnFor.job_id });
              }
            }
            void refresh();
          }}
        />
      )}
      {moveFor && (
        <MovePartDialog
          item={moveFor}
          onClose={() => setMoveFor(null)}
          onDone={(r) => { afterPart(r); void refresh(); }}
        />
      )}
      {undoUrl && (
        <UndoDialog previewUrl={undoUrl}
                    onClose={() => setUndoUrl(null)}
                    onDone={(msg) => { setNotice({ text: msg }); void refresh(); }} />
      )}

      {menuFor && (() => {
        const it = menuFor.item;
        const stage = stageById.get(it.stage_id);
        const isHeld = it.status === 'ON_HOLD';
        const out = it.outside_status ?? null;
        const canSkip = !out && it.status !== 'IN_PROGRESS' && stage && !stage.always_included;
        const canPlanOutside = !isHeld && !out && it.status === 'PENDING' && stage && !stage.always_included;
        const canMovePart = !out && !isHeld && !stage?.asks_quantity && Boolean(it.next_stage_name) && Number(it.ordered_quantity) > 1
          && !(it.card_part === 'CORRUGATION' && it.card_required && corrugationToDo(it));
        const pick = (kind: 'HOLD' | 'SKIP') => { setMenuFor(null); setReason(''); setActing({ item: it, kind }); };
        return (
          <>
            <div className="menu-scrim" onClick={() => setMenuFor(null)} />
            <div className="menu" role="menu"
                 style={{ position: 'fixed', top: menuFor.top, bottom: menuFor.bottom, right: menuFor.right, left: 'auto' }}>
              {canPlanOutside && (
                <>
                  <button role="menuitem"
                          onClick={() => { setMenuFor(null); setNotice(null); setPlanFor({ jobId: it.job_id, jobStageId: it.job_stage_id }); }}>
                    Send outside…
                  </button>
                  <div className="menu-sep" />
                </>
              )}
              {out === 'PLANNED' && (
                <>
                  <button role="menuitem"
                          onClick={() => { setMenuFor(null); setNotice(null); setPlanFor({ jobId: it.job_id }); }}>
                    Change outside plan…
                  </button>
                  <button role="menuitem" onClick={() => { setMenuFor(null); void removePlan(it); }}>
                    Remove outside plan
                  </button>
                  <div className="menu-sep" />
                </>
              )}
              {out === 'OUT' && (
                <>
                  <button role="menuitem" onClick={() => { setMenuFor(null); setNotice(null); setUndoOutFor(it); }}>
                    Undo send out…
                  </button>
                  <div className="menu-sep" />
                </>
              )}
              {canMovePart && (
                <>
                  <button role="menuitem" onClick={() => { setMenuFor(null); setNotice(null); setMoveFor(it); }}>
                    Move part…
                  </button>
                  <div className="menu-sep" />
                </>
              )}
              {!isHeld && out !== 'OUT' && <button role="menuitem" onClick={() => pick('HOLD')}>Put on hold…</button>}
              {canSkip && <button role="menuitem" onClick={() => pick('SKIP')}>Skip this stage…</button>}
              {(!isHeld || canSkip) && <div className="menu-sep" />}
              <button role="menuitem"
                      onClick={() => { setMenuFor(null); setUndoUrl(`/api/production/jobs/${it.job_id}/undo`); }}>
                Undo last step
              </button>
            </div>
          </>
        );
      })()}

      {acting && (() => {
        const it = acting.item;
        const hold = acting.kind === 'HOLD';
        const busy = busyId === it.job_stage_id;
        const go = () => { if (reason.trim()) void (hold ? doHold(it) : doSkip(it)); };
        return (
          <div className="backdrop" onClick={() => setActing(null)}>
            <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <h3>{hold ? `Put ${it.job_number} on hold` : `Skip ${it.stage_name}`}</h3>
                <p>{hold ? `At ${it.stage_name}` : it.job_number} · {it.customer_name} · {it.box_name}</p>
              </div>
              <div className="modal-body">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label htmlFor="reason">Reason</label>
                  <input id="reason" type="text" autoFocus value={reason}
                         onChange={(e) => setReason(e.target.value)}
                         onKeyDown={(e) => { if (e.key === 'Enter') go(); }}
                         placeholder={hold ? 'e.g. machine down, waiting on ink' : 'e.g. board came pre-coated'} />
                </div>
              </div>
              <div className="modal-foot">
                <button className="btn" onClick={() => setActing(null)} disabled={busy}>Cancel</button>
                <button className="btn primary" disabled={busy || !reason.trim()} onClick={go}>
                  {busy ? 'Saving…' : hold ? 'Put on hold' : 'Skip'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
